import {
  Button,
  Divider,
  Form,
  Input,
  Modal,
  Radio,
  Tabs,
  message as antdMessage,
} from "antd";
import type { ValidateErrorEntity } from "rc-field-form/es/interface";
import { useEffect, useMemo, useState } from "react";
import { useAppDispatch, useAppSelector } from "../../../../app/hooks";
import {
  ComplianceStatus,
  IBusinessFull,
} from "../../../../interfaces/businessFull.interface";
import {
  editBusinessThunk,
  editBusinessUserThunk,
  getBusinessThunk,
  selectCurrentBusiness,
  selectPosRTConfigs,
} from "../../businessesSlice";
import {
  getOnlinePricingSyncErrorMessage,
  isOnlinePricingRangeMessage,
  isOnlinePricingValidationError,
  resolveInvalidOnlinePricingBuckets,
  safeValidationMessage,
} from "../../utils/onlinePricingSyncErrors";
import BusinessUsersEditor from "../Business-Users-Editor-Modal/BusinessUsersEditor";
import {
  ApiOutlined,
  AppstoreOutlined,
  UserSwitchOutlined,
  CommentOutlined,
  SendOutlined,
  ExclamationCircleOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import { IBusinessUser } from "../../../../interfaces/businessUser.interface";
import CompanyAddressEditor from "./components/CompanyAddressEditor";
import BankAccountEditor from "./components/BankAccountEditor";
import PersonsEditor from "./components/PersonsEditor";

import CompanyProfileEditor from "./components/CompanyProfileEditor";
import { setAdyenPayoutScheduleServer } from "../../businessesApi";
import SettingsEditor from "./components/SettingsEditor";
import PayoutDetailsEditor from "./components/PayoutDetailsEditor";
import MerchantServiceFeesEditor from "./components/MerchantServiceFeesEditor";
import {
  changedOnlineBucketKeys,
  reconcileInPersonFeesForSave,
  withInPersonAmexFee,
} from "./components/merchantServiceFees.helpers";
import {
  describeBlockedSubmit,
  resolveEditorTabTarget,
} from "./businessEditorSubmitErrors";
import FlagEditor from "./components/FlagEditor";
import PartnerMapper from "./components/PartnerMapper";
import PartnerISVPricing from "./components/PartnerISVPricing";
import PricingNewTab from "./components/PricingNewTab";
import BusinessSensitiveActions from "./components/BusinessSensitiveActions";
import {
  selectIsKlearlyAdmin,
  selectIsKlearlySuperAdmin,
} from "../../../access-control/accountSlice";
import { selectHideInPersonAmex } from "../../../feature-flags/featureFlagsSlice";
import MccAndInustryEditor from "./components/MccAndInustryEditor";
import PosConnectEditor from "./components/PosConnectEditor";
import { isNoEdit } from "../../utils/isNoEdit";
import {
  isSixteenBucketMerchant,
  onlineGroupValueType,
  shouldLoadOnlinePricingPreview,
  sixteenBucketGroups,
} from "../../utils/sixteenBucketOnlinePricing";
import { hasUneditableOnlinePricing } from "../../utils/uneditableOnlinePricing";
import { useOnlinePricingPreview } from "./components/useOnlinePricingPreview";
import {
  clearPricingPrefetchCache,
  prefetchPricingTabData,
} from "./components/pricingPrefetch";
import { OnlineGroupKey } from "../../../../interfaces/onlinePricing.interface";
const { TextArea } = Input;

interface IPropsBusinessEditorModel {
  visible: boolean;
  setVisible: any;
  focusWebsiteSignal?: number;
}

export default function BusinessEditor({
  visible,
  setVisible,
  focusWebsiteSignal,
}: IPropsBusinessEditorModel) {
  const [loading, setLoading] = useState(false);
  const currentBusiness = useAppSelector(selectCurrentBusiness);
  const posRTConfigs = useAppSelector(selectPosRTConfigs);
  const isKlearlySuperAdmin = useAppSelector(selectIsKlearlySuperAdmin);
  const isKlearlyAdmin = useAppSelector(selectIsKlearlyAdmin);
  const hideInPersonAmex = useAppSelector(selectHideInPersonAmex);
  // Mirror CompanyProfileEditor: the Website field is only rendered for users
  // who can submit a merchant to Adyen onboarding.
  const canEditWebsite = isKlearlyAdmin;
  const [outerActiveKey, setOuterActiveKey] = useState("businessDetails");
  const [innerActiveKey, setInnerActiveKey] = useState("companyProfile");
  const [pendingErrorField, setPendingErrorField] = useState<
    (string | number)[] | null
  >(null);
  const [form] = Form.useForm();
  const dispatch = useAppDispatch();
  // The Modal stops re-rendering its content while closed, so a child never
  // sees `visible` go false. Count the opens and hand children that instead.
  const [openCount, setOpenCount] = useState(0);
  const [wasVisible, setWasVisible] = useState(visible);
  if (visible !== wasVisible) {
    setWasVisible(visible);
    if (visible) setOpenCount((count) => count + 1);
  }

  // PC-982: a 16-bucket merchant is priced on the groups its template pair
  // prices, not on the four legacy buckets. The group set is resolved here
  // rather than inside the fees editor so the rows the pane renders and the
  // keys the save sends can't drift apart.
  const isSixteenBucket = isSixteenBucketMerchant(currentBusiness);
  const isUneditableOnline = hasUneditableOnlinePricing(currentBusiness);
  const businessPartnerId =
    (currentBusiness?.partnerId as unknown as { _id?: string })?._id ??
    (currentBusiness?.partnerId as string | undefined);
  const storedOnlineProfileId =
    currentBusiness?.partnerOnlinePurchPriceProfileId || undefined;
  const loadOnlinePreview = shouldLoadOnlinePricingPreview({
    business: currentBusiness,
    partnerId: businessPartnerId,
  });
  const onlinePreview = useOnlinePricingPreview({
    businessId: currentBusiness?._id,
    partnerId: businessPartnerId,
    partnerOnlinePurchPriceProfileId: storedOnlineProfileId,
    enabled: isSixteenBucket && visible,
  });

  // Pricing (New) is lazy-mounted; start its partner/preview reads with the modal.
  useEffect(() => {
    if (!visible) {
      clearPricingPrefetchCache();
      return;
    }
    prefetchPricingTabData({
      businessId: currentBusiness?._id,
      partnerId: businessPartnerId,
      partnerOnlinePurchPriceProfileId: storedOnlineProfileId,
      preview: loadOnlinePreview,
    });
  }, [
    visible,
    currentBusiness?._id,
    businessPartnerId,
    storedOnlineProfileId,
    loadOnlinePreview,
  ]);
  const onlineGroups = useMemo(
    () =>
      isSixteenBucket
        ? sixteenBucketGroups(
            currentBusiness,
            onlinePreview.preview?.pricing?.onlineBucketGroupCoverage
          )
        : [],
    [
      isSixteenBucket,
      currentBusiness,
      onlinePreview.preview?.pricing?.onlineBucketGroupCoverage,
    ]
  );

  // When the parent asks to focus the Website field (e.g. after a submit-to-Adyen
  // "missing website" rejection), the field lives on the Company Profile inner
  // tab inside the Business Details outer tab. The Modal has no destroyOnClose
  // and both Tabs are otherwise uncontrolled, so it can reopen on a different
  // tab where the input is on a display:none pane and focus() would no-op. Drive
  // both tabs to the Website field before CompanyProfileEditor focuses it.
  useEffect(() => {
    if (!focusWebsiteSignal || !canEditWebsite) return;
    setOuterActiveKey("businessDetails");
    setInnerActiveKey("companyProfile");
  }, [focusWebsiteSignal, canEditWebsite]);

  useEffect(() => {
    form.resetFields();
    form.setFieldsValue({
      businessName: currentBusiness.businessName,
      businessTradeName: currentBusiness.businessTradeName,
      websiteURL: currentBusiness.websiteURL,
      supportPhone: currentBusiness.supportPhone,
      supportEmail: currentBusiness.supportEmail,
      businessRegistrationNumber: currentBusiness.businessRegistrationNumber,
      vatNumber: currentBusiness.vatNumber,
      tinNumber: currentBusiness.tinNumber,
      merchantServiceChargeFees: withInPersonAmexFee(
        currentBusiness.merchantServiceChargeFees,
        { hideInPersonAmex }
      ),
      onlinePricing: currentBusiness.onlinePricing,
      vat: currentBusiness.vat,
      merchantCategoryCode: currentBusiness.merchantCategoryCode,
      industry: currentBusiness.industry,
      name: currentBusiness.rootUser?.name,
      businessType: currentBusiness.businessType,
      payoutOptions: currentBusiness.payoutOptions
        ? currentBusiness.payoutOptions
        : { schedule: "daily" },
      bankAccountIban: currentBusiness.bankAccountIban,
      bic: currentBusiness.bic,
      bankAccountNumber: currentBusiness.bankAccountNumber,
      mccComments: currentBusiness.mccComments,
      generalComments: currentBusiness.generalComments,
      complianceContact: { ...currentBusiness.complianceContact },
      bankAddress: currentBusiness.bankAddress,
      bankName: currentBusiness.bankName,
      address: currentBusiness.address,
      onboardingFlags: currentBusiness.onboardingFlags,
      description: currentBusiness.description,
      //@ts-ignore
      partnerId: currentBusiness.partnerId?._id as string,
      partnerPurchPriceProfileId: currentBusiness.partnerPurchPriceProfileId,
      softwareVendorPricing: currentBusiness.softwareVendorPricing,
      softwareVendorPricingTemplate:
        currentBusiness.softwareVendorPricingTemplate,
      settings: {
        ...currentBusiness?.settings,

        reports: {
          ...currentBusiness?.settings?.reports,
          sendList:
            currentBusiness.settings?.reports?.sendList?.length > 0
              ? currentBusiness.settings?.reports?.sendList
              : [currentBusiness.rootUser?.email],
        },
      },
      persons: currentBusiness?.persons?.map((p) => ({
        ...p,
        dateOfBirth: p.dateOfBirth ? dayjs(p.dateOfBirth) : null,
      })),
    });

    // return () => {
    //   form.resetFields();
    // };
  }, [currentBusiness, hideInPersonAmex]);
  const handleSubmit = async (values: Partial<IBusinessFull>) => {
    // onlinePricing is the source of truth. The editor only edits each bucket's
    // value, so merge it back over the stored buckets (preserving valueType +
    // partner band min/max) and only send it when a value actually changed —
    // the backend re-derives the flattened display rows from it.
    if (values.merchantServiceChargeFees) {
      values.merchantServiceChargeFees = reconcileInPersonFeesForSave(
        values.merchantServiceChargeFees,
        currentBusiness.merchantServiceChargeFees,
        hideInPersonAmex
      );
    }
    // PC-1010: a 16-bucket merchant's online rates are not editable here, and
    // this endpoint is the wrong one to carry them: it re-derives every stored
    // per-bucket row from the group rate whenever the groups change, flattening
    // a bucket priced away from its group. So the save leaves online pricing
    // alone entirely and the divergence survives; Pricing (New) is the pane that
    // writes it, through the endpoint that accepts per-bucket rows.
    // A structure with no complete editor goes the same way: the five-key merge
    // below would $set away every group those keys cannot name.
    if (isSixteenBucket || isUneditableOnline) delete values.onlinePricing;

    // The keys a four-bucket save may carry: its five legacy buckets.
    const onlineKeys: OnlineGroupKey[] = [
      "iDeal",
      "amex",
      "corporateCreditCards",
      "nonCorporateCreditCards",
      "appleGooglePay",
    ];
    const editedOnline = values.onlinePricing as
      Record<string, { value?: number }> | undefined;
    let onlinePricingChanged = false;
    if (editedOnline) {
      const base = (currentBusiness.onlinePricing ?? {}) as Record<
        string,
        { value?: number; valueType?: string; min?: number; max?: number }
      >;
      onlinePricingChanged =
        changedOnlineBucketKeys(editedOnline, base, onlineKeys).length > 0;
      if (!onlinePricingChanged) {
        delete values.onlinePricing;
      } else {
        const merged: Record<string, unknown> = {};
        onlineKeys.forEach((k) => {
          const b = base[k] ?? {};
          merged[k] = {
            ...b,
            value: editedOnline[k]?.value ?? b.value ?? 0,
            valueType: b.valueType ?? onlineGroupValueType(k),
          };
        });
        values.onlinePricing = merged as never;
      }
    }

    // Submitted-to-Adyen: editing online pricing triggers a live Adyen resync,
    // so confirm before sending.
    const willSyncOnlinePricingToAdyen =
      isNoEdit(currentBusiness?.noEdit, "partnerId") &&
      Boolean(currentBusiness?.adyen?.splitConfigurationId);
    if (onlinePricingChanged && willSyncOnlinePricingToAdyen) {
      const confirmed = await new Promise<boolean>((resolve) => {
        Modal.confirm({
          title: "Update live Adyen pricing?",
          icon: <ExclamationCircleOutlined />,
          content: (
            <span>
              This merchant is live on Adyen. Saving updates their{" "}
              <b>online pricing directly in Adyen</b>, affecting real
              transactions. This can take a few seconds. Continue?
            </span>
          ),
          okText: "Update live pricing",
          cancelText: "Cancel",
          onOk: () => resolve(true),
          onCancel: () => resolve(false),
        });
      });
      if (!confirmed) return;
    }

    const fiscalization = values?.settings?.fiscalization;
    if (
      fiscalization &&
      (fiscalization.posRTConfig === "__none__" ||
        fiscalization.posRTConfig === undefined)
    ) {
      fiscalization.posRTConfig = null as unknown as string;
    }

    // Trim the website so trailing whitespace is not persisted and a
    // whitespace-only value collapses to "" (backend unsets the website).
    if (typeof values.websiteURL === "string") {
      values.websiteURL = values.websiteURL.trim();
    }

    // persons[] is submitted whole, so every save re-persists what was loaded.
    // Trim the address it would otherwise carry forward, and blank a country
    // code left by its initialValue on a person with no number.
    if (Array.isArray(values.persons)) {
      values.persons = values.persons.map((person) => {
        const next = { ...person };
        if (typeof next.email === "string") next.email = next.email.trim();
        if (!next.phone?.number) next.phone = { countryCode: "", number: "" };
        return next;
      });
    }

    setLoading(true);
    const editResult = await dispatch(
      editBusinessThunk({ _id: currentBusiness._id, updatedFields: values })
    );

    if (editBusinessThunk.rejected.match(editResult)) {
      const payload = editResult.payload as
        | { message?: string; field?: string; code?: string }
        | string
        | undefined;
      const message =
        typeof payload === "string"
          ? payload
          : payload?.message || "Update failed";
      const code = typeof payload === "object" ? payload?.code : undefined;
      const field =
        (typeof payload === "object" && payload?.field) ||
        (canEditWebsite && /websiteurl|website/i.test(message)
          ? "websiteURL"
          : undefined) ||
        (/vat/i.test(message) ? "vatNumber" : undefined) ||
        (/tin|fiscal|codice/i.test(message) ? "tinNumber" : undefined);

      if (
        field === "vatNumber" ||
        field === "tinNumber" ||
        field === "websiteURL"
      ) {
        form.setFields([{ name: field, errors: [message] }]);
      } else if (
        onlinePricingChanged &&
        (isOnlinePricingValidationError(code) ||
          isOnlinePricingRangeMessage(message))
      ) {
        // Out-of-range fee: attach our own contract message to the bucket
        // field(s) so the user can correct it. Nothing was persisted.
        const invalid = resolveInvalidOnlinePricingBuckets(payload, onlineKeys);
        const targets = invalid.length ? invalid : onlineKeys;
        setOuterActiveKey("businessDetails");
        setInnerActiveKey("merchantServiceFees");
        form.setFields(
          targets.map((k) => ({
            name: ["onlinePricing", k, "value"],
            errors: [safeValidationMessage(message)],
          }))
        );
      } else if (onlinePricingChanged) {
        // Any other online-pricing failure (incl. a live Adyen resync failure):
        // show clean copy that never leaks raw Adyen text, then refetch so the
        // display is honest whatever the backend actually committed.
        antdMessage.error(getOnlinePricingSyncErrorMessage(code));
        await dispatch(getBusinessThunk(currentBusiness._id));
      } else {
        antdMessage.error(message);
      }
      setLoading(false);
      return;
    }

    if (values && values?.persons && values?.persons[0]) {
      let updatedFields = {} as Partial<IBusinessUser>;
      let person1 = values?.persons[0];
      if (person1.address) updatedFields.address = person1.address;
      if (person1.dateOfBirth) updatedFields.dateOfBirth = person1.dateOfBirth;
      if (person1.firstName) updatedFields.firstName = person1.firstName;
      if (person1.lastName) updatedFields.lastName = person1.lastName;
      // A person with no phone leaves an empty-but-truthy object here, which
      // would overwrite the root user's number with a blank.
      if (person1.phone?.countryCode && person1.phone?.number)
        updatedFields.phone = person1.phone;

      if (values.businessRegistrationNumber)
        values.businessRegistrationNumber =
          values.businessRegistrationNumber.trim();

      await dispatch(
        editBusinessUserThunk({
          _id: currentBusiness.rootUser._id,
          updatedFields,
        })
      );
    }
    // Sync payout schedule to Adyen if frequency changed and Adyen is configured
    const newFrequency = values?.payoutOptions?.schedule;
    const oldFrequency = currentBusiness?.payoutOptions?.schedule;
    const adyen = currentBusiness?.adyen;
    if (
      newFrequency &&
      newFrequency !== oldFrequency &&
      adyen?.accountHolderId &&
      adyen?.balanceAccountId &&
      adyen?.transferInstrumentId
    ) {
      try {
        await setAdyenPayoutScheduleServer({
          businessId: currentBusiness._id,
          accountHolderId: adyen.accountHolderId,
          balanceAccountId: adyen.balanceAccountId,
          transferInstrumentId: adyen.transferInstrumentId,
          frequency: newFrequency,
        });
      } catch (error: any) {
        antdMessage.error(
          error?.response?.data?.message ||
            "Failed to update Adyen payout schedule"
        );
        setLoading(false);
        return;
      }
    }

    if (onlinePricingChanged && willSyncOnlinePricingToAdyen) {
      antdMessage.success("Online pricing updated and synced to Adyen.");
    }
    setLoading(false);
    setVisible(false);
  };
  // A rejected validateFields never reaches handleSubmit, and the offending
  // field can sit on a tab that isn't open — which is how a blocked Update used
  // to look like a dead button. Say what happened and go to the field.
  const handleSubmitFailed = ({ errorFields }: ValidateErrorEntity) => {
    const first = errorFields?.[0];
    const target = resolveEditorTabTarget(first?.name);
    if (target) {
      setOuterActiveKey(target.outer);
      if (target.inner) setInnerActiveKey(target.inner);
      setPendingErrorField(first.name);
    }
    antdMessage.error(describeBlockedSubmit(errorFields ?? []));
  };

  const handleGeneralCommentsSubmit = async () => {
    try {
      const values = await form.validateFields(["generalComments"]);
      await handleSubmit({ generalComments: values.generalComments });
    } catch (error) {
      const validationError = error as ValidateErrorEntity;
      if (validationError.errorFields) {
        handleSubmitFailed(validationError);
        return;
      }
      throw error;
    }
  };

  // Scroll only once the destination pane has rendered — the tab switch above
  // is what mounts it, so scrolling inside the handler would find nothing.
  useEffect(() => {
    if (!pendingErrorField) return;
    form.scrollToField(pendingErrorField, { block: "center" });
    setPendingErrorField(null);
  }, [pendingErrorField, form]);

  const handleCancel = () => {
    setVisible(false);
    // form.resetFields();
  };

  // Determine if the business is externally onboarding
  const isExternallyOnboarding =
    currentBusiness.externalOnboardingId &&
    ["pendingSubmission", "pendingVerification"].includes(
      currentBusiness.status
    );

  // Define which tabs should be hidden when external onboarding is active
  const hiddenTabs = isExternallyOnboarding
    ? ["companyAddress", "mccIndustry", "bankAccount", "persons", "flags"]
    : [];

  // List of all available tabs
  const tabItems = [
    {
      key: "companyProfile",
      label: "Company Profile",
      component: (
        <CompanyProfileEditor focusWebsiteSignal={focusWebsiteSignal} />
      ),
    },
    {
      key: "companyAddress",
      label: "Company Address",
      component: <CompanyAddressEditor />,
    },
    {
      key: "mccIndustry",
      label: "MCC & Industry",
      component: <MccAndInustryEditor />,
    },
    {
      key: "bankAccount",
      label: "Bank Account",
      component: <BankAccountEditor />,
    },
    {
      key: "persons",
      label: "Persons",
      component: <PersonsEditor form={form} />,
    },
    { key: "flags", label: "Flags", component: <FlagEditor form={form} /> },
    {
      key: "merchantServiceFees",
      label: "Merchant Service Fees",
      component: (
        <MerchantServiceFeesEditor
          onlineGroups={onlineGroups}
          previewOnlinePricing={onlinePreview.preview?.pricing?.onlinePricing}
          previewCoverage={
            onlinePreview.preview?.pricing?.onlineBucketGroupCoverage
          }
        />
      ),
    },
    {
      key: "payoutDetails",
      label: "Payout Details",
      component: <PayoutDetailsEditor />,
    },
    {
      key: "settings",
      label: "Settings",
      component: (
        <SettingsEditor
          currentBusiness={currentBusiness}
          form={form}
          posRTConfigs={posRTConfigs}
        />
      ),
    },
    {
      key: "partnerMapper",
      label: "Partner Mapper",
      component: <PartnerMapper />,
    },
    {
      key: "partnerISV",
      label: "Partner ISV",
      component: <PartnerISVPricing />,
    },
    {
      key: "pricingNew",
      label: "Pricing (New)",
      component: <PricingNewTab openCount={openCount} />,
    },
  ];

  return (
    <Modal
      visible={visible}
      title={"Update " + currentBusiness?.businessName}
      onCancel={handleCancel}
      footer={null}
      // Wide enough for the two-column pricing grid to show a full bucket name.
      width={1080}
    >
      <Tabs type="card" activeKey={outerActiveKey} onChange={setOuterActiveKey}>
        <Tabs.TabPane
          tab={
            <>
              <AppstoreOutlined /> Business Details
            </>
          }
          key="businessDetails"
        >
          <Form
            onFinish={handleSubmit}
            onFinishFailed={handleSubmitFailed}
            form={form}
          >
            <Tabs
              tabPosition="left"
              activeKey={innerActiveKey}
              onChange={setInnerActiveKey}
            >
              {tabItems
                .filter((tab) => !hiddenTabs.includes(tab.key)) // Filter using keys
                .map((tab) => (
                  <Tabs.TabPane tab={tab.label} key={tab.key}>
                    {tab.component}
                  </Tabs.TabPane>
                ))}
            </Tabs>
            {/* Pricing (New) saves via its own endpoint — hide the shared Update. */}
            {innerActiveKey !== "pricingNew" && (
              <Form.Item>
                <Button
                  style={{ float: "right" }}
                  type="primary"
                  htmlType="submit"
                  loading={loading}
                >
                  Update
                </Button>
              </Form.Item>
            )}
          </Form>
        </Tabs.TabPane>

        <Tabs.TabPane
          tab={
            <>
              <UserSwitchOutlined /> Users
            </>
          }
          key="users"
        >
          <BusinessUsersEditor />
        </Tabs.TabPane>

        <Tabs.TabPane
          tab={
            <>
              <CommentOutlined /> General Comments
            </>
          }
          key="generalComments"
        >
          <Form
            onFinish={handleSubmit}
            onFinishFailed={handleSubmitFailed}
            form={form}
          >
            <Form.Item name="generalComments" labelAlign="left">
              <TextArea
                showCount
                placeholder="Add comments here"
                autoSize={{ minRows: 8, maxRows: 12 }}
                maxLength={2000}
              />
            </Form.Item>
            <Form.Item>
              <Button
                style={{ float: "right", marginTop: "20px" }}
                type="primary"
                htmlType="button"
                onClick={handleGeneralCommentsSubmit}
                loading={loading}
              >
                Update
              </Button>
            </Form.Item>
          </Form>
        </Tabs.TabPane>

        <Tabs.TabPane
          tab={
            <>
              <SendOutlined /> Outreach
            </>
          }
          key="complianceContact"
        >
          <Form
            onFinish={handleSubmit}
            onFinishFailed={handleSubmitFailed}
            form={form}
          >
            <Form.Item name={["complianceContact", "status"]} labelAlign="left">
              <Radio.Group defaultValue={ComplianceStatus.NotContacted}>
                <Radio value={ComplianceStatus.NotContacted}>
                  {ComplianceStatus.NotContacted}
                </Radio>
                <Radio value={ComplianceStatus.ReachedOut}>
                  {ComplianceStatus.ReachedOut}
                </Radio>
                <Radio value={ComplianceStatus.InProgress}>
                  {ComplianceStatus.InProgress}
                </Radio>
                <Radio value={ComplianceStatus.Completed}>
                  {ComplianceStatus.Completed}
                </Radio>
              </Radio.Group>
            </Form.Item>
            <Form.Item name={["complianceContact", "notes"]} labelAlign="left">
              <TextArea
                showCount
                placeholder="Add comments here"
                autoSize={{ minRows: 8, maxRows: 12 }}
                maxLength={500}
              />
            </Form.Item>
            <Form.Item>
              <Button
                style={{ float: "right", marginTop: "20px" }}
                type="primary"
                htmlType="submit"
                loading={loading}
              >
                Update
              </Button>
            </Form.Item>
          </Form>
        </Tabs.TabPane>

        <Tabs.TabPane
          tab={
            <>
              <ApiOutlined /> POS Connect
            </>
          }
          key="posConnect"
        >
          <PosConnectEditor />
        </Tabs.TabPane>

        {isKlearlySuperAdmin && (
          <Tabs.TabPane
            tab={
              <>
                <SendOutlined /> Sensitive Actions
              </>
            }
            key="sensitive_actions"
          >
            <BusinessSensitiveActions
              business={currentBusiness}
              setVisible={setVisible}
            />
          </Tabs.TabPane>
        )}
      </Tabs>
    </Modal>
  );
}
